import { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footer from "components/footers/Footer";
import Swal from 'sweetalert2';
import './style.css';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaUserEdit } from "react-icons/fa";
import * as XLSX from 'xlsx';

const UserAdmin = () => {
    const { t } = useTranslation();
    const [suppliers, setSuppliers] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deletedSuppliers, setDeletedSuppliers] = useState(new Set()); 
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        checkAdminStatus();
    }, []);

    useEffect(() => {
        if (isAdmin) {
            fetchAllSuppliers();
        }
    }, [currentPage, isAdmin]);

    const checkAdminStatus = () => {
        const isAdminFlag = localStorage.getItem('isAdmin') === '1';
        setIsAdmin(isAdminFlag);
        if (isAdminFlag) {
            fetchAllSuppliers();
        }
    };

    const fetchAllSuppliers = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`https://jolyaparis.cloud/api/admin/users?page=${currentPage}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                setSuppliers(response.data.data); 
                setTotalPages(response.data.last_page); 
            }
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Excel Export Feature
    const exportToExcel = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            Swal.fire({
                title: t('Préparation de l\'export...'),
                text: t('Récupération de toutes les données en cours, veuillez patienter...'),
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            let allUsers = [];
            let page = 1;
            let hasMorePages = true;

            while (hasMorePages) {
                const url = `https://jolyaparis.cloud/api/admin/users?page=${page}`;
                const response = await axios.get(url, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.status === 200 && response.data) {
                    const pageData = response.data.data || [];
                    if (pageData.length === 0) {
                        hasMorePages = false;
                    } else {
                        allUsers = [...allUsers, ...pageData];
                        if (page >= (response.data.last_page || 1)) {
                            hasMorePages = false;
                        } else {
                            page++;
                        }
                    }
                } else {
                    hasMorePages = false;
                }
            }

            Swal.close();

            if (allUsers.length === 0) {
                Swal.fire("Info", "Aucune donnée à exporter", "info");
                return;
            }

            const totalFiltered = allUsers.filter(supplier =>
                (supplier.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (supplier.custom_id?.toLowerCase().includes(searchTerm.toLowerCase()))
            );

            const excelData = totalFiltered.map(supplier => ({
                "Identifiant": supplier.custom_id || '',
                "Nom": supplier.name || '',
                "Email": supplier.email || '',
                "Numéro de Téléphone": supplier.phone || '',
                "Adresse": supplier.address || '',
                "Rôle": supplier.role === 'supplier' ? 'Distributeur' : (supplier.role || 'Aucun rôle'),
                "Date de création": supplier.created_at ? new Date(supplier.created_at).toLocaleString() : ''
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Tous les Utilisateurs");

            XLSX.writeFile(workbook, `Utilisateurs_Jolyaparis_${new Date().toISOString().slice(0,10)}.xlsx`);

        } catch (error) {
            Swal.close();
            Swal.fire("Erreur", "Impossible de récupérer toutes les pages de données.", "error");
            console.error("Excel Export Error: ", error);
        }
    };

    if (!isAdmin) {
        return <p className="text-danger text-center">Unauthorized: You do not have admin access.</p>;
    }

    const filteredSuppliers = suppliers.filter(supplier =>
        (supplier.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (supplier.custom_id?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const banUser = async (supplierId) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.delete(
                `https://jolyaparis.cloud/api/admin/user/${supplierId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                setDeletedSuppliers((prev) => new Set(prev.add(supplierId)));
                Swal.fire("Success", "Utilisateur supprimé avec succés!", "success");
                fetchAllSuppliers();
            } else {
                Swal.fire("Error", "échec de la suppression", "error");
            }
        } catch (error) {
            Swal.fire("Error", error.response?.data || "Something went wrong", "error");
            fetchAllSuppliers();
        }
    };

    const ActiverUserAccount = async (supplierId) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.put(
                `https://jolyaparis.cloud/api/admin/activerUser/${supplierId}`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                Swal.fire("Success", "Utilisateur activé avec succés!", "success");
                fetchAllSuppliers();
            } else {
                Swal.fire("Error", "échec d'activer ce compte", "error");
            }
        } catch (error) {
            Swal.fire("Error", error.response?.data || "Something went wrong", "error");
            fetchAllSuppliers();
        }
    };

    // Delete User functionality
    const deleteUser = async (userId) => {
        const confirm = await Swal.fire({
            title: "Êtes-vous sûr ?",
            text: "Cette action est irréversible !",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        });
        if (!confirm.isConfirmed) return;

        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.delete(`https://jolyaparis.cloud/api/admin/delete/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.status === 200) {
                Swal.fire("Supprimé !", "Utilisateur supprimé avec succès.", "success");
                fetchAllSuppliers();
            }
        } catch (error) {
            Swal.fire("Erreur", error.response?.data?.message || "Échec de la suppression", "error");
        }
    };

    // Change Role functionality
    const changeUserRole = async (userId, currentRole) => {
        const { value: role } = await Swal.fire({
            title: "Changer le rôle",
            input: "select",
            inputOptions: {
                supplier: "Distributeur",
                influenceur: "Influenceur",
                client_fidele: "client_fidele",
            },
            inputValue: currentRole,
            showCancelButton: true,
            confirmButtonText: "Changer",
            cancelButtonText: "Annuler",
        });
        if (!role) return;

        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.put(`https://jolyaparis.cloud/api/admin/user/${userId}/role`, { role }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.status === 200) {
                Swal.fire("Succès", "Rôle modifié avec succès", "success");
                fetchAllSuppliers();
            }
        } catch (error) {
            Swal.fire("Erreur", error.response?.data?.message || "Échec de la modification", "error");
        }
    };

    return (
        <AnimationRevealPage>
            <LightAdmin />
            <div className="d-flex flex-column flex-md-row">
                <section className="w-100 p-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <h1 className="text-dark display-5 m-0">{t('Tableau_de_bord_Utilisateurs')}</h1>
                        
                        {/* Search and Excel Export container */}
                        <div className="d-flex gap-2 align-items-center">
                            <button 
                                className="btn btn-success" 
                                onClick={exportToExcel}
                                style={{ fontWeight: '500', whiteSpace: 'nowrap' }}
                            >
                                📊 {t('Exporter en Excel')}
                            </button>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('Rechercher_par_nom_id')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ maxWidth: '300px' }}
                            />
                        </div>
                    </div>
                    <hr className="my-4" />

                    {/* Suppliers Table */}
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="table-primary">
                                <tr>
                                    <th>{t('Identifiant')}</th>
                                    <th>{t('Nom')}</th>
                                    <th>{t('Email')}</th>
                                    <th>{t('Numéro_de_Téléphone')}</th>
                                    <th>{t('Adresse')}</th>
                                    <th>{t('Role')}</th>
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSuppliers && filteredSuppliers.length > 0 ? (
                                    filteredSuppliers.map((supplier) => (
                                        <tr key={supplier.id}>
                                            <td>{supplier.custom_id}</td>
                                            <td>{supplier.name}</td>
                                            <td>{supplier.email}</td>
                                            <td>{supplier.phone}</td>
                                            <td>{supplier.address}</td>
                                            <td>
                                                {supplier.role === 'supplier' ? 'Distributeur' : supplier.role ?? 'Aucun rôle'}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                                    <button
                                                        style={{ backgroundColor: '#000000', color: 'white' }}
                                                        className="btn btn-sm"
                                                        onClick={() => banUser(supplier.id)}
                                                        disabled={supplier.is_deleted}
                                                    >
                                                        {t('Interdire')}
                                                    </button>
                                                    <button
                                                        style={{ backgroundColor: '#edbf03', color: 'white' }}
                                                        className="btn btn-sm"
                                                        onClick={() => ActiverUserAccount(supplier.id)}
                                                        disabled={supplier.compteVerified}
                                                    >
                                                        {t('Activer')}
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-danger btn-sm" 
                                                        onClick={() => deleteUser(supplier.id)} 
                                                        title="Supprimer l'utilisateur"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-primary btn-sm" 
                                                        title="Changer le rôle" 
                                                        onClick={() => changeUserRole(supplier.id, supplier.role)}
                                                    >
                                                        <FaUserEdit />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center">Aucun utilisateur trouvé.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="d-flex justify-content-center">
                        <nav>
                            <ul className="pagination">
                                <li className="page-item" onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}>
                                    <button className="page-link" disabled={currentPage === 1}>{t('Previous')}</button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => (
                                    <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className="page-item" onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}>
                                    <button className="page-link" disabled={currentPage === totalPages}>{t('Next')}</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </section>
            </div>
            <Footer />
        </AnimationRevealPage>
    );
};

export default UserAdmin;